// React & React Hooks
import { useContext, useEffect, useState } from "react";

// Third-party libraries
import _ from "lodash";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// MUI components
import { styled } from "@mui/material/styles";
import {
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

// MUI icons
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ZoomInMapSharpIcon from "@mui/icons-material/ZoomInMapSharp";
import ZoomOutMapSharpIcon from "@mui/icons-material/ZoomOutMapSharp";

// MUI x-tree-view
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Constants
import urlConstant from "../../constants/urlConstants";

// Services / utilities
import { httpPost } from "../../services/commonService";

// Redux
import { selectUserDetail } from "../../redux/slice/userSlice";

const CustomTextFieldLeft = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "1rem",
    color: "var(--black)",
    marginBottom: theme.spacing(0.5),
  },
}));

let userData = [];
let treeDataList = [];
const SponsorTree1 = () => {
  const userDetail = useSelector(selectUserDetail);
  const authenticationState = useContext(AuthenticationContext);
  const [treeData, setTreeData] = useState([]);
  const [lastNodeAcctId, setLastNodeAcctId] = useState();
  const { t } = useTranslation();
  const [dialogData, setDialogData] = useState(null);
  const [userAcctId, setUserAcctId] = useState(0);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isApicalled, setIsApicalled] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.AcctID) {
      const getSponsorInfoURL = urlConstant.ReferralTree.GetReferralTreeList;
      const mainAccount = {
        acctID: userDetail.UserAccountDetails.AcctID,
        refID: userDetail.UserAccountDetails.DirectSponsorDetails.DSAcctID,
        RankID: userDetail.UserAccountDetails.RankID,
        RankName: userDetail.UserAccountDetails.RankName,
        AcctName: userDetail.UserAccountDetails.PvtAcctFullName,
        AcctStatus: userDetail.UserAccountDetails.AcctStatus,
        UserId: userDetail.UserAccountDetails.UserID,
        AchievedRankName: userDetail.UserAccountDetails.AchievedRankName,
        PackageType: userDetail.PurchasePackageDetails.LatestPackageType,
      };
      httpPost(
        getSponsorInfoURL,
        {},
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          const dataMap = {};
          const responseData = [mainAccount, ...response.Value];
          responseData.forEach((item) => {
            dataMap[item.acctID] = {
              ...item,
              children: [],
              isHighlight: false,
              PackageTypeStatus: item.PackageType?.toLowerCase().includes(
                "[inactive]"
              )
                ? "inactive"
                : "active",
            };
          });
          setUserAcctId(userDetail.UserAccountDetails.AcctID);
          const tree = [];
          responseData.forEach((item) => {
            if (dataMap[item.refID]) {
              dataMap[item.refID].children.push(dataMap[item.acctID]);
            } else {
              tree.push(dataMap[item.acctID]);
            }
          });
          userData = dataMap;
          setTreeData(tree);
          setExpandedItems([userDetail.UserAccountDetails.AcctID?.toString()]);
          treeDataList = tree;
          setLastNodeAcctId(tree[0]?.children?.at(-1)?.acctID);
          setIsApicalled(true);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [userDetail]);

  useEffect(() => {
    if (isApicalled) {
      handleSearch(searchText);
    }
  }, [searchText]);

  const iconColor = (packageType = "") => {
    const type = packageType.toLowerCase();
    const colorMap = {
      affiliate: "#58B84A",
      "consultant (bylder)": "#FFB752",
      consultant: "#6D9AE2",
      "aventi customer": "#ee5d2c",
      "aventi travel": "#283841",
    };
    if (type.includes("[inactive]")) return "#FF6C6C";
    return colorMap[type] || "#FFFFFF";
  };

  const buildRichTreeData = (nodes = []) =>
    nodes.map((node) => ({
      ...node,
      id: node.acctID.toString(),
      label: `${node.AcctName} (${node.acctID})`,
      children: node.children?.length ? buildRichTreeData(node.children) : [],
    }));

  const handleExpandAll = () => {
    const expandWorker = new Worker(
      new URL("../../workers/treeWorker.js", import.meta.url)
    );

    expandWorker.postMessage({
      userData: treeDataList,
      searchTerm: "",
      userAcctId,
      isExpandAll: true,
    });

    expandWorker.onmessage = function (event) {
      const { expandedIds } = event.data;
      setExpandedItems(expandedIds);
      expandWorker.terminate();
    };
  };

  const handleCollapseAll = () => {
    setExpandedItems([userAcctId.toString()]);
  };

  const updateHighlights = (nodes = null) => {
    if (!nodes) {
      for (const key in userData) {
        userData[key].isHighlight = false;
      }
      return;
    }
    const traverse = (nodeList) => {
      for (let i = 0; i < nodeList.length; i++) {
        const node = nodeList[i];
        const userNode = userData[node.acctID];
        if (userNode) {
          userNode.isHighlight = node.isHighlight;
        }
        if (node.children?.length) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
  };

  const handleSearch = (searchEmail) => {
    if (searchEmail === "") {
      setTreeData(treeDataList);
      handleExpandAll();
      updateHighlights();
      return;
    }
    const searchWorker = new Worker(
      new URL("../../workers/treeWorker.js", import.meta.url)
    );
    searchWorker.postMessage({
      userData,
      searchTerm: searchEmail,
      userAcctId,
      isExpandAll: false,
    });
    searchWorker.onmessage = function (event) {
      const { treeData, expandedIds } = event.data;
      updateHighlights(treeData);
      setTreeData(treeData);
      setExpandedItems(expandedIds);
      searchWorker.terminate();
    };
  };

  const handleClickOpen = (node) => {
    setDialogData(node);
    setOpen(true);
  };

  const CustomNodeForRichTree = ({ item, color, onClick }) => {
    const isLastSponsorNode =
      item?.acctID === lastNodeAcctId && Array.isArray(item.children);
    return (
      <Stack className={isLastSponsorNode ? "last-sponsor-tree" : ""}>
        <Box
          sx={(theme) => ({
            background: color,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 2,
            px: 2,
            py: 1,
            border: "1px solid var(--headerbg)",
            position: "relative",
            zIndex: "1",
            minWidth: { xs: "100%", sm: "300px" },
            width: { xs: "100%", sm: "auto" },
          })}
          onClick={(e) => {
            e.stopPropagation();
            onClick(item);
          }}
        >
          <Button
            variant="contained"
            sx={{
              px: { xs: 1, sm: 0.8 },
              py: { xs: 1, sm: 0.5 },
              background: iconColor(item?.PackageType),
              minWidth: "auto",
            }}
            disableElevation
          >
            <img src="/icons/user-header.svg" alt="user" />
          </Button>
          <Typography variant="body2" className="tree-text" fontWeight="600">
            {`${item?.AcctName ?? "Unknown"} (${item?.acctID ?? "?"})`}
          </Typography>
          <Chip
            label={item?.children?.length ?? 0}
            size="medium"
            className="numbered"
            sx={{
              pointerEvents: "none",
              bgcolor: "var(--secondary-main)",
              color: "var(--black)",
              fontSize: "16px",
              borderRadius: "10px",
              marginLeft: "auto",
              fontWeight: 600,
              "& .MuiChip-label": {
                padding: "7px",
                minWidth: "35px",
                textAlign: "center",
              },
            }}
          />
        </Box>
      </Stack>
    );
  };

  const CustomRichTreeItem = ({ itemId, children, userData, onNodeClick }) => {
    const item = userData[itemId];
    const isLast =  Array.isArray(item.children) &&  item.children.findIndex(child => child.acctID === item?.acctID) == item.children.length - 1 ;
    return (
      <TreeItem
        itemId={itemId}
        className={`cm-tree-inner ${isLast ? " cm-last-child" : ""}`}
        label={
          <CustomNodeForRichTree
            item={item}
            color={
              item?.isHighlight
                ? "var(--primary-darker)"
                : "var(--tree-gradient)"
            }
            onClick={onNodeClick}
          />
        }
      >
        {children}
      </TreeItem>
    );
  };

  return (
    <>
      <Box width={"100%"}>
        <Typography
          color="secondary.light"
          display={{ xs: "block", md: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
          mb={"20px"}
        >
          {t("My Team")}
        </Typography>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Typography
            color="secondary.light"
            mb={"20px"}
            variant="h4"
            fontWeight={600}
          >
            {t("SponsorTree.SponsorTree")}
          </Typography>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.common.black,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Stack
                direction="row"
                flexWrap={"wrap"}
                sx={{
                  gap: { xs: "10px", lg: "50px" },
                  justifyContent: { xs: "flex-start", sm: "center" },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("[Inactive]"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Inactive accounts
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("consultant (bylder)"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    BYLDers
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("consultant"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Consultants
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("affiliate"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Affiliates
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("aventi customer"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Aventi Travel
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("aventi travel"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Aventi Travel Business
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Card
              sx={(theme) => ({
                p: "20px",
                background: theme.palette.common.headerbg,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                boxShadow: "none",
                my: "20px",
                width: "100%",
                flexDirection: { xs: "column", sm: "row" },
              })}
            >
              <Stack
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", sm: "auto" },
                }}
                gap={2}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleExpandAll}
                  startIcon={<ZoomOutMapSharpIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {t("SponsorTree.Expand all")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCollapseAll}
                  startIcon={<ZoomInMapSharpIcon />}
                >
                  {t("SponsorTree.Collapse all")}
                </Button>
              </Stack>
              <Stack
                className="customfield-label-left cm-normal-search"
                sx={{
                  maxWidth: "700px",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "flex-end",
                }}
              >
                <Card className="customfield-label-inner">
                  <SearchOutlinedIcon />
                </Card>
                <CustomTextFieldLeft
                  fullWidth
                  variant="outlined"
                  onChange={(e) => {
                    const handler = setTimeout(() => {
                      setSearchText(e.target.value);
                    }, 1000);

                    return () => {
                      clearTimeout(handler);
                    };
                  }}
                />
              </Stack>
            </Card>
            <Box
              sx={{
                minHeight: 352,
                display: "inline-block",
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
                textAlign:
                  searchText && treeData.length === 0 ? "center" : "initial",
              }}
            >
              {searchText && treeData.length === 0 ? (
                <Typography color="secondary.text" sx={{ mt: 2 }}>
                  {t("SponsorTree.NoRecordsFound")}
                </Typography>
              ) : (
                <RichTreeView
                  items={buildRichTreeData(treeData)}
                  expandedItems={expandedItems}
                  onExpandedItemsChange={(event, itemIds) =>
                    setExpandedItems(itemIds)
                  }
                  selectedItems={selectedItems}
                  onSelectedItemsChange={(event, itemIds) =>
                    setSelectedItems(itemIds)
                  }
                  isItemExpandable={(item) =>
                    Boolean(item.children && item.children.length > 0)
                  }
                  getChildren={(item) => item.children ?? []}
                  slots={{
                    item: (props) => (
                      <CustomRichTreeItem
                        {...props}
                        userData={userData}
                        onNodeClick={handleClickOpen}
                      />
                    ),
                  }}
                />
              )}
            </Box>
          </Card>
        </Card>
      </Box>
      <Dialog
        onClick={() => setOpen(false)}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {t("SponsorTree.UserDetail")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={(theme) => ({
            background: theme.palette.background.cardGradient,
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          })}
          dividers
        >
          <Button
            variant="contained"
            sx={{
              px: 0.8,
              py: { xs: 2, sm: 0.5 },
              width: "auto",
              backgroundColor: iconColor(dialogData?.PackageType),
            }}
            color="primary"
            disableElevation
          >
            <img src="/icons/user-header.svg" alt="user" />
          </Button>
          <Stack textAlign={"center"}>
            <Typography
              color="common.light"
              sx={{}}
              variant="h4"
              fontWeight={600}
            >
              {`${t("SponsorTree.Hi")} ${dialogData?.AcctName}`}
            </Typography>
            <Card
              className="bg-dark"
              sx={{
                py: 1,
                px: 2,
                textAlign: "center",
                width: {
                  xs: "100%",
                  xxl: "auto",
                },
                mt: "10px",
              }}
            >
              <Typography color="secondary.light" variant="body1">
                {dialogData?.RankName}
              </Typography>
            </Card>
          </Stack>
          <TableContainer
            sx={{ background: "transparent", boxShadow: "none" }}
            component={Paper}
          >
            <Table sx={{ background: "transparent" }} aria-label="simple table">
              <TableBody>
                <TableRow
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                    "& td,& th": {
                      borderBottom: "1px solid var(--black) !important",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                      paddingTop: "7px",
                      paddingBottom: "7px",
                    },
                  }}
                >
                  <TableCell>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <Typography
                        color="secondary.text"
                        sx={{ fontSize: "12px" }}
                        variant="body2"
                      >
                        {t("SponsorTree.SponsoredBy")}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell valign="middle" align="right">
                    <Typography
                      color="common.white"
                      sx={{}}
                      variant="body1"
                      fontWeight={600}
                    >
                      {dialogData?.refID}
                    </Typography>
                  </TableCell>
                </TableRow>
                {/* Conditional rendering based on PackageType for inactive status */}
                {dialogData?.PackageType?.toLowerCase().includes(
                  "[inactive]"
                ) ? (
                  <>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Status")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          sx={
                            dialogData?.AcctStatus?.toLowerCase().includes(
                              "inactive"
                            )
                              ? { color: "#FF6C6C" }
                              : { color: "common.white" }
                          }
                          variant="body1"
                          fontWeight={600}
                        >
                          {"Inactive"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Date")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.ExpirationDate
                            ? new Date(
                                dialogData.ExpirationDate
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                              })
                            : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Level")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.PackageType}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.AchievementStatus")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.AchievedRankName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default SponsorTree1;